import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserProfiles } from '@/hooks/useUserProfiles';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, DollarSign, FileText, Loader2, Shield, Clock, CheckCircle, XCircle, ArrowLeft, MessageSquareWarning } from 'lucide-react';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface Dispute {
  id: string;
  transaction_id: string;
  stripe_dispute_id: string;
  amount_cents: number;
  currency: string;
  reason: string;
  status: string;
  evidence: string | null;
  evidence_due_by: string | null;
  created_at: string;
  updated_at: string;
}

interface Transaction {
  id: string;
  conversation_id: string;
  client_id: string;
  provider_id: string;
  amount_cents: number;
  currency: string;
  status: string;
  created_at: string;
  payment_intent_id: string;
}

interface Refund {
  id: string;
  transaction_id: string;
  stripe_refund_id: string;
  amount_cents: number;
  currency: string;
  reason: string | null;
  status: string;
  created_at: string;
}

interface RefundRequest {
  id: string;
  transaction_id: string;
  requester_id: string;
  amount_cents: number;
  reason: string;
  detailed_explanation: string | null;
  status: string;
  admin_decision: string | null;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

const AdminRefunds = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getUserProfile, profileCache } = useUserProfiles();
  
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [refunds, setRefunds] = useState<Refund[]>([]);
  const [refundRequests, setRefundRequests] = useState<RefundRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Refund dialog state
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundType, setRefundType] = useState<'full' | 'partial'>('full');
  const [refundLoading, setRefundLoading] = useState(false);
  
  // Evidence dialog state
  const [evidenceDialogOpen, setEvidenceDialogOpen] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [evidenceText, setEvidenceText] = useState('');
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  // Refund request review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<RefundRequest | null>(null);
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'denied' | 'partial'>('approved');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Fetch disputes
      const { data: disputeData } = await supabase
        .from('payment_disputes')
        .select('*')
        .order('created_at', { ascending: false });
      setDisputes(disputeData || []);

      // Fetch transactions (for refund capability)
      const { data: txData } = await supabase
        .from('payment_transactions')
        .select('id, conversation_id, client_id, provider_id, amount_cents, currency, status, created_at, payment_intent_id')
        .order('created_at', { ascending: false })
        .limit(100);
      setTransactions(txData || []);

      // Fetch refunds
      const { data: refundData } = await supabase
        .from('payment_refunds')
        .select('*')
        .order('created_at', { ascending: false });
      setRefunds(refundData || []);

      // Fetch refund requests
      const { data: requestData } = await supabase
        .from('refund_requests')
        .select('*')
        .order('created_at', { ascending: false });
      setRefundRequests(requestData || []);

      // Load user profiles
      const userIds = new Set<string>();
      txData?.forEach(t => {
        userIds.add(t.client_id);
        userIds.add(t.provider_id);
      });
      requestData?.forEach(r => {
        userIds.add(r.requester_id);
      });
      for (const id of userIds) {
        if (!profileCache[id]) await getUserProfile(id);
      }
    } catch (e) {
      console.error('Error fetching data:', e);
    }
  }, [getUserProfile, profileCache]);

  useEffect(() => {
    const checkAdminAndFetchData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const { data: adminCheck } = await supabase.rpc('is_user_admin', { target_user_id: user.id });
        setIsAdmin(!!adminCheck);
        
        if (adminCheck) {
          await fetchData();
        }
      } catch (e) {
        console.error('Error checking admin status:', e);
      } finally {
        setLoading(false);
      }
    };

    checkAdminAndFetchData();
  }, [user, fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!isAdmin) return;
    
    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [isAdmin, fetchData]);

  const handleRefund = async () => {
    if (!selectedTransaction) return;
    
    setRefundLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-refund', {
        body: {
          transaction_id: selectedTransaction.id,
          amount_cents: refundType === 'partial' ? Math.round(parseFloat(refundAmount) * 100) : undefined,
          reason: refundReason,
          refund_type: refundType
        }
      });

      if (error) throw error;
      
      toast.success('Refund processed successfully');
      setRefundDialogOpen(false);
      setSelectedTransaction(null);
      setRefundAmount('');
      setRefundReason('');
      await fetchData();
    } catch (e: any) {
      console.error('Refund error:', e);
      toast.error(e.message || 'Failed to process refund');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleReviewRequest = async () => {
    if (!selectedRequest || !user) return;

    setReviewLoading(true);
    try {
      const tx = transactions.find(t => t.id === selectedRequest.transaction_id);

      // IMPORTANT: Never mark a request "approved" unless the Stripe refund succeeds.
      if (reviewDecision === 'approved') {
        if (!tx) throw new Error('Transaction not found for this refund request');

        const { error: refundError } = await supabase.functions.invoke('process-refund', {
          body: {
            transaction_id: tx.id,
            reason: `Approved refund request: ${selectedRequest.reason}`,
            refund_type: 'full',
            // Pass refund requester info to be stored on transaction
            refund_requester_id: selectedRequest.requester_id,
            refund_requested_at: selectedRequest.created_at
          }
        });

        if (refundError) throw refundError;
      }

      // Update request only AFTER Stripe succeeds (or immediately for deny/partial).
      // For 'approved', we mark the request as reviewed but the TRUE status is on the transaction.
      const { error: updateError } = await supabase
        .from('refund_requests')
        .update({
          status: reviewDecision === 'approved' ? 'reviewed' : reviewDecision,
          admin_decision: reviewDecision,
          admin_notes: reviewNotes,
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id);

      if (updateError) throw updateError;

      toast.success(`Refund request ${reviewDecision}`);
      setReviewDialogOpen(false);
      setSelectedRequest(null);
      setReviewNotes('');
      await fetchData();
    } catch (e: any) {
      console.error('Review error:', e);
      toast.error(e.message || 'Failed to process review');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleSubmitEvidence = async () => {
    if (!selectedDispute) return;
    
    setEvidenceLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('submit-dispute-evidence', {
        body: {
          dispute_id: selectedDispute.id,
          evidence: {
            uncategorized_text: evidenceText,
            submit_now: true
          }
        }
      });

      if (error) throw error;
      
      toast.success('Evidence submitted successfully');
      setEvidenceDialogOpen(false);
      setSelectedDispute(null);
      setEvidenceText('');
      await fetchData();
    } catch (e: any) {
      console.error('Evidence error:', e);
      toast.error(e.message || 'Failed to submit evidence');
    } finally {
      setEvidenceLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline', icon: any }> = {
      needs_response: { variant: 'destructive', icon: AlertTriangle },
      warning_needs_response: { variant: 'destructive', icon: AlertTriangle },
      under_review: { variant: 'secondary', icon: Clock },
      won: { variant: 'default', icon: CheckCircle },
      lost: { variant: 'destructive', icon: XCircle },
      charge_refunded: { variant: 'outline', icon: DollarSign },
      refunded: { variant: 'outline', icon: DollarSign },
      pending: { variant: 'secondary', icon: Clock },
      approved: { variant: 'default', icon: CheckCircle },
      denied: { variant: 'destructive', icon: XCircle },
    };
    const config = variants[status] || { variant: 'secondary' as const, icon: Clock };
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status.replace(/_/g, ' ')}
      </Badge>
    );
  };

  const getReasonLabel = (reason: string) => {
    const labels: Record<string, string> = {
      service_not_provided: 'Service Not Provided',
      service_unsatisfactory: 'Service Unsatisfactory',
      provider_unresponsive: 'Provider Unresponsive',
      billing_error: 'Billing Error',
      other: 'Other'
    };
    return labels[reason] || reason;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-background py-12">
        <div className="container mx-auto px-4 text-center">
          <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Admin Access Required</h1>
          <p className="text-muted-foreground">You don't have permission to view this page.</p>
        </div>
      </div>
    );
  }

  const activeDisputes = disputes.filter(d => ['needs_response', 'warning_needs_response', 'under_review'].includes(d.status));
  const pendingRequests = refundRequests.filter(r => {
    const tx = transactions.find(t => t.id === r.transaction_id);
    const isRefunded = tx?.status === 'refunded' || tx?.status === 'partially_refunded';
    return !isRefunded && r.status !== 'denied';
  });

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin-dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Refunds & Disputes</h1>
            <p className="text-muted-foreground">Review refund requests and manage payment disputes</p>
          </div>
        </div>

        {(activeDisputes.length > 0 || pendingRequests.length > 0) && (
          <Card className="mb-6 border-destructive">
            <CardHeader className="pb-3">
              <CardTitle className="text-destructive flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                {pendingRequests.length > 0 && `${pendingRequests.length} Pending Refund Request${pendingRequests.length > 1 ? 's' : ''}`}
                {pendingRequests.length > 0 && activeDisputes.length > 0 && ' • '}
                {activeDisputes.length > 0 && `${activeDisputes.length} Active Dispute${activeDisputes.length > 1 ? 's' : ''}`}
              </CardTitle>
            </CardHeader>
          </Card>
        )}

        <Tabs defaultValue="requests">
          <TabsList className="mb-4">
            <TabsTrigger value="requests" className="relative">
              Requests
              {pendingRequests.length > 0 && (
                <span className="ml-2 bg-destructive text-destructive-foreground text-xs px-1.5 py-0.5 rounded-full">
                  {pendingRequests.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="refunds">Refunds ({refunds.length})</TabsTrigger>
            <TabsTrigger value="disputes">Disputes ({disputes.length})</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
          </TabsList>

          <TabsContent value="requests">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquareWarning className="h-5 w-5" />
                  Refund Requests
                </CardTitle>
                <CardDescription>User-submitted refund requests requiring admin review</CardDescription>
              </CardHeader>
              <CardContent>
                {refundRequests.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No refund requests</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Requester</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {refundRequests.map(request => {
                        const tx = transactions.find(t => t.id === request.transaction_id);
                        const isRefunded = tx?.status === 'refunded' || tx?.status === 'partially_refunded';
                        const effectiveStatus = isRefunded
                          ? 'refunded'
                          : request.status === 'denied'
                            ? 'denied'
                            : 'pending';

                        const canReview = !isRefunded && request.status !== 'denied';
                        const actionLabel = request.status === 'approved' && !isRefunded ? 'Retry' : 'Review';

                        return (
                          <TableRow key={request.id}>
                            <TableCell>{getStatusBadge(effectiveStatus)}</TableCell>
                            <TableCell className="font-medium">${(request.amount_cents / 100).toFixed(2)}</TableCell>
                            <TableCell>{profileCache[request.requester_id]?.name || 'Loading...'}</TableCell>
                            <TableCell>{getReasonLabel(request.reason)}</TableCell>
                            <TableCell>{formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}</TableCell>
                            <TableCell>
                              {canReview ? (
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setReviewDialogOpen(true);
                                  }}
                                >
                                  {actionLabel}
                                </Button>
                              ) : (
                                <span className="text-sm text-muted-foreground">
                                  {isRefunded ? 'Refunded' : (request.admin_decision || request.status)}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="refunds">
            <Card>
              <CardHeader>
                <CardTitle>Refund History</CardTitle>
                <CardDescription>All processed refunds</CardDescription>
              </CardHeader>
              <CardContent>
                {refunds.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No refunds processed</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Amount</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {refunds.map(refund => (
                        <TableRow key={refund.id}>
                          <TableCell className="font-medium">${(refund.amount_cents / 100).toFixed(2)}</TableCell>
                          <TableCell>{refund.reason || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={refund.status === 'succeeded' ? 'default' : 'secondary'}>
                              {refund.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{format(new Date(refund.created_at), 'MMM d, yyyy HH:mm')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="disputes">
            <Card>
              <CardHeader>
                <CardTitle>Payment Disputes</CardTitle>
                <CardDescription>Chargebacks and disputes filed by customers</CardDescription>
              </CardHeader>
              <CardContent>
                {disputes.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No disputes found</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Status</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Evidence Due</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {disputes.map(dispute => (
                        <TableRow key={dispute.id}>
                          <TableCell>{getStatusBadge(dispute.status)}</TableCell>
                          <TableCell className="font-medium">${(dispute.amount_cents / 100).toFixed(2)}</TableCell>
                          <TableCell className="capitalize">{dispute.reason.replace(/_/g, ' ')}</TableCell>
                          <TableCell>
                            {dispute.evidence_due_by ? (
                              <span className={new Date(dispute.evidence_due_by) < new Date() ? 'text-destructive' : ''}>
                                {formatDistanceToNow(new Date(dispute.evidence_due_by), { addSuffix: true })}
                              </span>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{format(new Date(dispute.created_at), 'MMM d, yyyy')}</TableCell>
                          <TableCell>
                            {['needs_response', 'warning_needs_response'].includes(dispute.status) && (
                              <Button 
                                size="sm" 
                                onClick={() => { setSelectedDispute(dispute); setEvidenceDialogOpen(true); }}
                              >
                                <FileText className="h-4 w-4 mr-1" /> Submit Evidence
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
                <CardDescription>Issue refunds for completed transactions</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Amount</TableHead>
                      <TableHead>Client</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.slice(0, 50).map(tx => (
                      <TableRow key={tx.id}>
                        <TableCell className="font-medium">${(tx.amount_cents / 100).toFixed(2)}</TableCell>
                        <TableCell>{profileCache[tx.client_id]?.name || 'Loading...'}</TableCell>
                        <TableCell>{profileCache[tx.provider_id]?.name || 'Loading...'}</TableCell>
                        <TableCell>
                          <Badge variant={tx.status === 'completed' ? 'default' : 'secondary'}>
                            {tx.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{format(new Date(tx.created_at), 'MMM d, yyyy')}</TableCell>
                        <TableCell>
                          {['completed', 'disputed'].includes(tx.status) && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => { setSelectedTransaction(tx); setRefundDialogOpen(true); }}
                            >
                              <DollarSign className="h-4 w-4 mr-1" /> Refund
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Refund Dialog */}
        <Dialog open={refundDialogOpen} onOpenChange={setRefundDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Process Refund</DialogTitle>
              <DialogDescription>
                Refund ${((selectedTransaction?.amount_cents || 0) / 100).toFixed(2)} transaction
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Refund Type</Label>
                <Select value={refundType} onValueChange={(v: 'full' | 'partial') => setRefundType(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full">Full Refund</SelectItem>
                    <SelectItem value="partial">Partial Refund</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {refundType === 'partial' && (
                <div>
                  <Label>Amount (USD)</Label>
                  <Input 
                    type="number" 
                    step="0.01" 
                    value={refundAmount} 
                    onChange={(e) => setRefundAmount(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              )}
              <div>
                <Label>Reason</Label>
                <Textarea 
                  value={refundReason} 
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="Reason for refund..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRefundDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleRefund} disabled={refundLoading}>
                {refundLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Process Refund
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Evidence Dialog */}
        <Dialog open={evidenceDialogOpen} onOpenChange={setEvidenceDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Submit Dispute Evidence</DialogTitle>
              <DialogDescription>
                Provide evidence to contest this ${((selectedDispute?.amount_cents || 0) / 100).toFixed(2)} dispute
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Evidence / Response</Label>
                <Textarea 
                  value={evidenceText} 
                  onChange={(e) => setEvidenceText(e.target.value)}
                  placeholder="Describe the service provided, include any relevant details..."
                  rows={6}
                />
              </div>
              {selectedDispute?.evidence_due_by && (
                <p className="text-sm text-muted-foreground">
                  Due: {format(new Date(selectedDispute.evidence_due_by), 'MMM d, yyyy HH:mm')}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEvidenceDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitEvidence} disabled={evidenceLoading || !evidenceText.trim()}>
                {evidenceLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit Evidence
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Review Request Dialog */}
        <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Review Refund Request</DialogTitle>
              <DialogDescription>
                {selectedRequest && (
                  <>
                    Amount: ${(selectedRequest.amount_cents / 100).toFixed(2)} • 
                    Reason: {getReasonLabel(selectedRequest.reason)}
                  </>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {selectedRequest?.detailed_explanation && (
                <div>
                  <Label className="text-muted-foreground">User's Explanation</Label>
                  <p className="mt-1 p-3 bg-muted rounded-md text-sm">{selectedRequest.detailed_explanation}</p>
                </div>
              )}
              <div>
                <Label>Decision</Label>
                <Select value={reviewDecision} onValueChange={(v: 'approved' | 'denied' | 'partial') => setReviewDecision(v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approved">Approve (Full Refund)</SelectItem>
                    <SelectItem value="denied">Deny</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Admin Notes (optional)</Label>
                <Textarea 
                  value={reviewNotes} 
                  onChange={(e) => setReviewNotes(e.target.value)}
                  placeholder="Internal notes about this decision..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleReviewRequest} 
                disabled={reviewLoading}
                variant={reviewDecision === 'denied' ? 'destructive' : 'default'}
              >
                {reviewLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {reviewDecision === 'approved' ? 'Approve & Refund' : 'Deny Request'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminRefunds;
